/**
 * Composed goal-recipe catalog entries for the corporate industry (plan §6.3 /
 * §6.5). Filled by the corporate P4b copywriting task — owned exclusively by it.
 * Entries omit `previewPath` (added automatically in template-catalog.ts).
 *
 * `features` lists only what the composed seed actually generates for each
 * goal — verified against `apps/cms/src/seeds/__tests__/__snapshots__/
 * composed-corporate.snapshot.test.ts.snap` (pages created / forms created /
 * `skippedRoles`) at authoring time:
 *  - `corporate-trust`: home + people + contact pages, contact form created.
 *    `proof` is skipped (base pack's secondary page is a services page, not
 *    a track record) — no "実績" claim in this entry's features.
 *  - `corporate-recruit`: home + about + recruit + contact pages, contact
 *    (応募)form created. Built on the `corporate-recruiting` pack, so every
 *    role resolves (no skips).
 *  - `corporate-discovery`: home page only (the `access` page is dropped —
 *    corporate packs carry no structured address/hours data). No form.
 *  - `corporate-sell`: home + offering + order pages. The order page hosts
 *    the generic contact form (`forms.order` aliases `forms.contact` in the
 *    corporate adapter) — a consultation/estimate request form, not an
 *    EC/checkout flow, and the copy below keeps that distinction.
 */
import type { TemplateEntryDraft } from '../template-catalog';

const CORPORATE_SUB_INDUSTRIES = [
  '総合',
  '製造業',
  'IT・ソフトウェア',
  '建設・工務店',
  '採用強化',
  '地域密着サービス',
] as const;

export const COMPOSED_CORPORATE_TEMPLATES: readonly TemplateEntryDraft[] = [
  {
    slug: 'corporate-trust',
    theme: 'corporate',
    displayName: '会社の実績と人柄で選ばれるコーポレートサイト',
    category: '会社・コーポレート',
    description:
      'ホーム上部で会社概要・代表者情報を前面に出し、こだわり紹介からお問合せまでを一本道でつなぐ構成。会社情報の専用ページも備え、「どんな会社か」を知ってから相談してもらう流れをつくります。',
    thumb: '/lp/template-corporate.webp',
    previewFixture: 'acme',
    fit: '実績や会社の信頼性で選ばれたい中小企業、初めての取引先に安心してもらいたい会社',
    features: [
      'ホーム上部に会社概要・代表者情報',
      '会社情報の専用ページ',
      '事業の特長・強み紹介',
      'お問合せフォーム（スパム対策・通知メール付き）',
    ],
    goal: 'trust',
    industry: 'corporate',
    subIndustries: [...CORPORATE_SUB_INDUSTRIES],
    outcomeStory:
      '初めて会社名を知った相手が最初に気にするのは「どんな会社が、何をしているか」です。このサイトはホームの目立つ位置に会社概要と代表者の紹介を置き、事業の特長を伝えたうえでお問合せへとつなげます。会社情報は専用ページも用意されているので、取引の検討段階で「もう少し詳しく知りたい」と思った相手にも、安心して読んでもらえる材料が揃っています。',
    concerns: [
      {
        worry: '会社の魅力をどう文章にすればいいか分からない',
        answer:
          'そのまま公開できる初期文言があらかじめ入っています。まずは会社名・所在地・代表者名など、自社の情報に書き換えるところから始められます。',
      },
      {
        worry: '実績や事例がまだ少ない',
        answer:
          'この構成は会社概要と代表者の人柄で信頼を伝える構成です。事例紹介は含まれないため、実績づくりの途中でも公開いただけます。',
      },
      {
        worry: '問い合わせにきちんと対応できるか不安',
        answer:
          'お問合せフォームには通知メールとスパム対策が付いています。配信に障害が起きた場合でも、フォームの受付自体は継続する仕組みです。',
      },
    ],
    businessFlow: [
      '公開前に会社概要ページへ所在地・代表者・沿革などの情報を入力する',
      '事業の特長やこだわりを3つに整理してホームに掲載する',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      'お問合せフォームに届いた内容を確認し、担当者から折り返し連絡する',
    ],
  },
  {
    slug: 'corporate-recruit',
    theme: 'corporate',
    displayName: '応募したくなる会社の顔をつくる採用サイト',
    category: '会社・コーポレート',
    description:
      'ホーム上部で働く魅力を伝え、募集要項の専用ページと応募フォームまでを一つの導線でつなぐ構成。会社情報ページも備え、「どんな会社で、どう働けるか」を知ったうえで応募してもらえます。',
    thumb: '/lp/template-corporate-recruiting.webp',
    previewFixture: 'recruiting-demo',
    fit: '採用を強化したい中小企業、求人媒体だけでなく自社サイトでも応募を受け付けたい会社',
    features: [
      'ホーム上部に働く魅力紹介',
      '募集要項の専用ページ（募集職種・待遇）',
      '会社情報の専用ページ',
      '応募・お問合せフォーム（スパム対策・通知メール付き）',
    ],
    goal: 'recruit',
    industry: 'corporate',
    subIndustries: [...CORPORATE_SUB_INDUSTRIES],
    outcomeStory:
      '求人媒体だけでは伝えきれない社風や働く人の雰囲気を、自社サイトなら自分たちの言葉で伝えられます。このサイトはホームで働く魅力を紹介したあと、募集職種・待遇を専用ページでまとめて見せ、応募フォームへとつなげます。会社情報ページも備えているので、応募を迷っている方が「どんな会社か」をもう一段深く確認してから、安心して応募ボタンを押せます。',
    concerns: [
      {
        worry: '働く魅力や社風をどう見せればいいか分からない',
        answer:
          'そのまま公開できる初期文言があらかじめ入っています。自社の研修制度や働き方の実際の内容に差し替えるだけで形になります。',
      },
      {
        worry: '募集要項の更新が続けられるか不安',
        answer:
          'かんたん更新で募集職種や待遇の内容を編集できます。入力後はダッシュボードの「サイトを公開」ボタンを押すだけで反映されます。',
      },
      {
        worry: '応募が来たときに対応が漏れないか心配',
        answer:
          '応募フォームには通知メールとスパム対策が付いています。配信に障害が起きた場合でも、フォームの受付自体は継続する仕組みです。',
      },
    ],
    businessFlow: [
      '公開前に募集職種・待遇・福利厚生を専用ページに入力する',
      '働く魅力やホームの紹介文を自社の実際の制度・エピソードに書き換える',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      '応募フォームの通知メールを確認し、担当者から選考の案内を連絡する',
    ],
  },
  {
    slug: 'corporate-discovery',
    theme: 'corporate',
    displayName: '新しいお客様に会社の存在を知ってもらうサイト',
    category: '会社・コーポレート',
    description:
      'ホーム1ページに会社の特長を簡潔にまとめた構成。検索で初めて社名を知った方に、事業内容と信頼できる会社であることを短時間で伝えます。まず存在を知ってもらうことを目的にした最小構成です。',
    thumb: '/lp/template-corporate-local-service.webp',
    previewFixture: 'local-service-demo',
    fit: '検索や紹介で初めて社名を知ってもらいたい会社、まずは最小構成で公開したい会社',
    features: ['事業の特長・強み紹介', '事業紹介の抜粋', 'そのまま使える初期文言', '高速表示'],
    goal: 'discovery',
    industry: 'corporate',
    subIndustries: [...CORPORATE_SUB_INDUSTRIES],
    outcomeStory:
      '取引先候補や採用候補が社名で検索したとき、まず知りたいのは「実在する会社か、何をしているか」です。このサイトはホーム1ページに事業の特長と概要を簡潔にまとめているので、初めて訪れた方でも短時間で「信頼できそうな会社だ」と判断してもらえます。まずは最小構成で公開し、必要に応じてページを増やしていく土台として使えます。',
    concerns: [
      {
        worry: '会社の紹介文を書く時間がない',
        answer:
          'そのまま公開できる初期文言があらかじめ入っています。会社名など固有の情報だけ書き換えれば、すぐに公開できる状態になっています。',
      },
      {
        worry: 'まずは最小限で始めたいが、後から追加できるか不安',
        answer:
          'かんたん更新でページや事業内容を後から追加できます。会社の成長に合わせて少しずつ内容を充実させていただけます。',
      },
      {
        worry: '表示が遅いとすぐ離脱されてしまわないか心配',
        answer: '高速表示に対応した構成なので、検索から訪れた方を待たせません。',
      },
    ],
    businessFlow: [
      '公開前に事業の特長・強みを3つに整理してホームに入力する',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      '名刺やSNSのプロフィールにサイトURLを載せて、検索からの入口を増やす',
      '事業内容が増えたら、かんたん更新でホームの紹介を書き足していく',
    ],
  },
  {
    slug: 'corporate-sell',
    theme: 'corporate',
    displayName: 'サービス相談につなげる事業紹介サイト',
    category: '会社・コーポレート',
    description:
      'ホームで事業・サービスの概要を紹介し、事業紹介の専用ページで詳しく伝えたうえで、ご相談ページへとつなげる構成。BtoB・法人向けサービスの相談・引き合いを増やしたい会社に向いています。',
    thumb: '/lp/template-corporate-it.webp',
    previewFixture: 'it-demo',
    fit: '自社サービス・事業内容への相談や引き合いを増やしたい会社、複数の事業を展開している会社',
    features: [
      '事業・サービス紹介の抜粋（ホーム）',
      '事業・サービス紹介の専用ページ',
      'ご相談・お見積りフォーム付きのご相談ページ',
      'そのまま使える初期文言',
    ],
    goal: 'sell',
    industry: 'corporate',
    subIndustries: [...CORPORATE_SUB_INDUSTRIES],
    outcomeStory:
      'サービスへの興味は、ホームで概要を知り、専用ページで詳しく読んだうえで「相談してみよう」と思うところまで進んで初めて問い合わせにつながります。このサイトはホームに事業紹介の抜粋を置き、専用ページで主な事業・サービスを詳しく伝えたあと、ご相談ページへの動線を用意しています。複数の事業を展開している会社でも、サービスごとの強みを整理して見せられる構成です。',
    concerns: [
      {
        worry: 'サービスの魅力をどう文章にすればいいか分からない',
        answer:
          'そのまま公開できる初期文言があらかじめ入っています。まずは提供しているサービスの内容や対象のお客様を書き換えるところから始められます。',
      },
      {
        worry: '事業内容が複数あって、整理して見せられるか不安',
        answer:
          '事業・サービス紹介ページは複数のサービスを並べて紹介できる構成になっています。かんたん更新で内容を追加・編集できます。',
      },
      {
        worry: '相談・問い合わせが来たときの受け皿があるか心配',
        answer:
          'ご相談ページには問い合わせ種別まで選べるご相談フォームが最初から入っています。スパム対策と通知メール付きで、届いた内容はメールで確認できます。',
      },
    ],
    businessFlow: [
      '公開前に事業・サービス紹介ページへ主なサービス内容を入力する',
      'ホームの事業紹介の抜粋を、専用ページと矛盾のない内容に整える',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      '営業資料や名刺にサイトURLを載せて、ご相談ページへの入口を増やす',
    ],
  },
];
